// biome-ignore-all lint/a11y/noLabelWithoutControl: staff-only admin surface; these labels are visual captions for the adjacent control, intentionally not wired via htmlFor (Task #25).

import React from "react";
import { listStaffDocs } from "../../data/storage";
import { ROLES } from "../../utils/enums";
import { isStaffEmailDomain } from "../../utils/loginMethod";
import { isValidEmail } from "../../utils/sanitize";
import { Btn } from "../../utils/ui.jsx";
import { ModalBackdrop } from "../form/FormUI.jsx";
import { StaffDocumentsModal } from "./StaffDocumentsModal.jsx";

const ROLE_LABELS = {
  staff: "Responder",
  picker: "Picker",
  admin: "Supervisor",
  superuser: "SuperUser",
  combined: "Combined",
};
function roleLabel(r) {
  return ROLE_LABELS[r] || r;
}

// Login method is DERIVED from the email domain (docs/google-sso-plan.md §0), not
// stored or chosen: a Zephyr Workspace address uses Google SSO, everyone else uses
// password + authenticator. Display-only. Gated on the SSO switch, so it reads
// "Password + Authenticator" for everyone until SSO is turned on.
function derivedLoginMethodLabel(email) {
  return import.meta.env.VITE_GOOGLE_SSO === "true" && isStaffEmailDomain(email)
    ? "Google Workspace SSO"
    : "Password + Authenticator";
}

function actionBtn(label, onClick, colour) {
  return (
    <button
      onClick={onClick}
      className="bg-transparent rounded-[6px] py-1 px-[10px] text-[11px] cursor-pointer"
      style={{
        border: `1px solid ${colour || "#CFE0DE"}`,
        color: colour || "#1A2E2B",
        fontFamily: "inherit",
      }}
    >
      {label}
    </button>
  );
}

// The Edit form, rendered inside a modal (ModalBackdrop) in the shared admin
// modal style — matches ShelterDetail's user edit + SupplierFormModal.
function StaffEditModal({
  editing,
  form,
  fv,
  setF,
  saving,
  saveEdit,
  setEditing,
  sendPasswordReset,
  resetUserMfa,
  canManageDocs,
}) {
  // db-208: the Documents window (SuperUsers only — `canManageDocs`).
  const [docsOpen, setDocsOpen] = React.useState(false);
  // How many documents this person has — shown on the button, like the DFV Service
  // Documents button. Loaded once for SuperUsers; null = not loaded / failed (no count).
  const [docCount, setDocCount] = React.useState(null);
  const profileId = editing?.id;
  React.useEffect(
    function () {
      if (!canManageDocs || !profileId) return undefined;
      let alive = true;
      listStaffDocs(profileId).then(function (rows) {
        if (alive) setDocCount((rows || []).length);
      });
      return function () {
        alive = false;
      };
    },
    [canManageDocs, profileId]
  );
  // Block Save on an unsendable address (email-validation-plan §6A).
  const emailValue = String(fv(form, "user_email") || "");
  const emailInvalid = emailValue.trim() !== "" && !isValidEmail(emailValue);
  return (
    <ModalBackdrop
      portal
      onClose={function () {
        setEditing(null);
      }}
      contentStyle={{ maxWidth: 920 }}
    >
      <div
        className="bg-white rounded-[10px] py-[18px] px-[22px]"
        style={{ width: 880, maxWidth: "100%" }}
      >
        <div className="font-bold text-[17px] text-zephyr-teal-dark mb-4">Editing Zephyr Staff</div>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div>
            <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
              First Name
            </label>
            <input
              value={fv(form, "first_name")}
              onChange={function (e) {
                setF("first_name", e.target.value);
              }}
              className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
              style={{ fontFamily: "inherit" }}
            />
          </div>
          <div>
            <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
              Last Name
            </label>
            <input
              value={fv(form, "last_name")}
              onChange={function (e) {
                setF("last_name", e.target.value);
              }}
              className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
              style={{ fontFamily: "inherit" }}
            />
          </div>
          <div>
            <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
              Login Email{" "}
              <span className="text-[10px] text-zephyr-muted font-normal normal-case">
                (updates their login)
              </span>
            </label>
            <input
              type="email"
              value={fv(form, "user_email")}
              onChange={function (e) {
                setF("user_email", e.target.value);
              }}
              title="Saving with a different email will update both auth.users and profiles via the admin-users Edge Function. The user will need to log in with the new email."
              className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
              style={{ fontFamily: "inherit" }}
            />
            {emailInvalid && (
              <p role="alert" className="text-[11px] text-zephyr-red mt-1 mb-0">
                Not a valid email address — please check it.
              </p>
            )}
          </div>
          <div>
            <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
              Role
            </label>
            <select
              value={fv(form, "role")}
              onChange={function (e) {
                setF("role", e.target.value);
              }}
              className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
              style={{ fontFamily: "inherit" }}
            >
              {["staff", "picker", "admin", "superuser", "combined"].map(function (r) {
                return (
                  <option key={r} value={r}>
                    {roleLabel(r)}
                  </option>
                );
              })}
            </select>
          </div>
          <div>
            <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
              Login Method
            </label>
            <div
              className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-muted bg-[#EEF3F2]"
              style={{ fontFamily: "inherit" }}
              title="Set automatically by the email domain — not editable."
            >
              {derivedLoginMethodLabel(fv(form, "user_email"))}
            </div>
          </div>
          <div>
            <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
              Mobile
            </label>
            <input
              value={fv(form, "staff_mobile")}
              onChange={function (e) {
                setF("staff_mobile", e.target.value);
              }}
              placeholder="e.g. 0400 123 456"
              className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
              style={{ fontFamily: "inherit" }}
            />
          </div>
          <div>
            <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
              Display Name
            </label>
            <input
              value={fv(form, "preferred_name")}
              onChange={function (e) {
                setF("preferred_name", e.target.value);
              }}
              placeholder="e.g. John W (Zephyr)"
              className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
              style={{ fontFamily: "inherit" }}
            />
          </div>
          <div>
            <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
              Staff Colour
            </label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                aria-label="Staff colour picker"
                value={fv(form, "staff_colour") || "#1A3B5D"}
                onChange={function (e) {
                  setF("staff_colour", e.target.value);
                }}
                style={{
                  width: 42,
                  height: 34,
                  border: "1px solid #CFE0DE",
                  borderRadius: 8,
                  padding: 0,
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              />
              <input
                value={fv(form, "staff_colour")}
                onChange={function (e) {
                  setF("staff_colour", e.target.value);
                }}
                placeholder="#1A3B5D"
                className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                style={{ fontFamily: "inherit" }}
              />
            </div>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-[13px] text-zephyr-text cursor-pointer pb-2">
              <input
                type="checkbox"
                checked={fv(form, "show_on_staff_list") !== false}
                onChange={function (e) {
                  setF("show_on_staff_list", e.target.checked);
                }}
              />
              Show on Staff List
            </label>
          </div>
        </div>
        {/* Consultant — a Zephyr person on a non-Zephyr email domain (e.g.
            john@luxor8.com). Zephyr staff are otherwise always
            @zephyreducation.com.au, so this flag is what lets the code recognise a
            consultant as a ZEPHYR person rather than guessing from their address.
            See docs/staff-caseworker-separation-plan.md §4 — a Zephyr consultant may
            never be a DFV Service contact, exactly like an employee. */}
        <div className="mb-4">
          <label className="flex items-center gap-2 text-[13px] text-zephyr-text cursor-pointer">
            <input
              type="checkbox"
              checked={fv(form, "is_consultant") === true}
              onChange={function (e) {
                setF("is_consultant", e.target.checked);
              }}
            />
            Consultant
            <span className="text-[12px] text-zephyr-muted">
              — a Zephyr person whose email is not @zephyreducation.com.au
            </span>
          </label>
        </div>
        <div className="flex gap-[10px] flex-wrap">
          <Btn
            variant="secondary"
            onClick={function () {
              setEditing(null);
            }}
          >
            Cancel
          </Btn>
          <Btn
            onClick={function () {
              sendPasswordReset(fv(form, "user_email"));
            }}
            variant="secondary"
          >
            📧 Password Reset Email
          </Btn>
          <Btn
            onClick={function () {
              resetUserMfa(editing.id, fv(form, "user_email"));
            }}
            variant="secondary"
          >
            🔐 Reset MFA
          </Btn>
          {canManageDocs && (
            <Btn
              onClick={function () {
                setDocsOpen(true);
              }}
              variant="secondary"
            >
              {`📄 Documents${docCount ? ` (${docCount})` : ""}`}
            </Btn>
          )}
          <Btn onClick={saveEdit} disabled={saving || emailInvalid}>
            {saving ? "Saving..." : "Save Staff"}
          </Btn>
        </div>
      </div>
      {canManageDocs && docsOpen && (
        <StaffDocumentsModal
          profileId={editing.id}
          staffName={`${fv(form, "first_name")} ${fv(form, "last_name")}`.trim()}
          onCountChange={setDocCount}
          onClose={function () {
            setDocsOpen(false);
          }}
        />
      )}
    </ModalBackdrop>
  );
}

export function UserAdminStaffList({
  filteredStaff,
  editing,
  setEditing,
  viewing,
  setViewing,
  form,
  fv,
  setF,
  saving,
  saveEdit,
  startEdit,
  sendPasswordReset,
  resetUserMfa,
  setConfirmDeactivate,
  canManageDocs,
}) {
  const editingProfile = editing && editing.table === "profiles";
  return (
    <div className="border border-zephyr-border rounded-[10px] overflow-hidden">
      <div
        className="grid gap-0 bg-zephyr-teal-dark text-white py-[10px] px-[18px] text-[11px] font-bold uppercase tracking-[0.5px]"
        style={{ gridTemplateColumns: "1fr 160px 140px 220px 100px 160px" }}
      >
        <div>Name</div>
        <div>Display Name</div>
        <div>Mobile</div>
        <div>Email</div>
        <div>Role</div>
        <div>Actions</div>
      </div>
      {filteredStaff.map(function (u, idx) {
        const isViewing = viewing === u.id;
        const roleColour =
          u.role === ROLES.SUPERUSER
            ? "#1A237E" // deep indigo
            : u.role === ROLES.ADMIN
              ? "#00695C" // Supervisor — teal
              : u.role === ROLES.PICKER
                ? "#E65100" // Picker — orange
                : u.role === ROLES.COMBINED
                  ? "#9C27B0" // Combined — purple
                  : u.role === ROLES.STAFF
                    ? "#D32F2F" // Responder — red (John's call: keep the roles visually distinct)
                    : "#5C6BC0";
        const displayName =
          [u.staff_first_name, u.staff_last_name].filter(Boolean).join(" ") ||
          u.display_name ||
          u.staff_user_email ||
          "—";
        return (
          <div
            key={u.id}
            style={
              isViewing
                ? {
                    // Bound the selected (view) region clearly.
                    border: "2px solid #00897B",
                    borderRadius: 10,
                    margin: "8px 0",
                    background: "#F0F7F6",
                    boxShadow: "0 2px 14px rgba(0,137,123,0.18)",
                  }
                : {
                    borderBottom: idx < filteredStaff.length - 1 ? "1px solid #CFE0DE" : "none",
                    background: u.deactivated ? "#FFF3F3" : idx % 2 === 0 ? "#FFFFFF" : "#F7F9F9",
                  }
            }
          >
            <div>
              <div
                className="grid gap-0 py-[10px] px-[18px] items-center"
                style={{ gridTemplateColumns: "1fr 160px 140px 220px 100px 160px" }}
              >
                <div>
                  <button
                    type="button"
                    onClick={function () {
                      setViewing(isViewing ? null : u.id);
                      if (editing) setEditing(null);
                    }}
                    className="bg-transparent border-0 p-0 font-semibold text-[13px] underline cursor-pointer text-left"
                    style={{
                      color: u.deactivated ? "#607D79" : "#1A2E2B",
                      fontFamily: "inherit",
                    }}
                  >
                    {displayName}
                  </button>
                  {u.deactivated && (
                    <span className="ml-[6px] text-[10px] font-bold text-white bg-[#C62828] rounded-[3px] py-[1px] px-[6px]">
                      DEACTIVATED
                    </span>
                  )}
                </div>
                <div className="text-[12px] text-zephyr-muted overflow-hidden text-ellipsis whitespace-nowrap">
                  {u.preferred_name || "—"}
                </div>
                <div className="text-[12px] text-zephyr-muted">{u.staff_mobile || "—"}</div>
                <div className="text-[12px] text-zephyr-muted overflow-hidden text-ellipsis whitespace-nowrap">
                  {u.staff_user_email || "—"}
                </div>
                <div>
                  <span
                    className="text-[11px] font-bold text-white rounded px-2 py-[2px]"
                    style={{ background: roleColour }}
                  >
                    {roleLabel(u.role)}
                  </span>
                </div>
                <div className="flex gap-[5px]">
                  {actionBtn(
                    "Edit",
                    function () {
                      setViewing(null);
                      startEdit(u, "profiles");
                    },
                    null
                  )}
                  <button
                    onClick={function () {
                      setConfirmDeactivate(u);
                    }}
                    className="bg-transparent rounded-[6px] py-1 px-[10px] text-[11px] cursor-pointer"
                    style={{
                      border: `1px solid ${u.deactivated ? "#2E7D32" : "#EF5350"}`,
                      color: u.deactivated ? "#2E7D32" : "#C62828",
                      fontFamily: "inherit",
                    }}
                  >
                    {u.deactivated ? "Reactivate" : "Deactivate"}
                  </button>
                </div>
              </div>
              {isViewing && (
                <div className="py-4 px-5 border-t border-zephyr-border bg-zephyr-teal-light">
                  <div className="grid grid-cols-3 gap-4 mb-3">
                    <div>
                      <div className="text-[10px] font-bold text-zephyr-muted uppercase tracking-[0.5px] mb-[3px]">
                        Login Email
                      </div>
                      <div className="text-[13px]">{u.staff_user_email || "—"}</div>
                    </div>
                    <div>
                      <div className="text-[10px] font-bold text-zephyr-muted uppercase tracking-[0.5px] mb-[3px]">
                        Role
                      </div>
                      <div className="text-[13px]">{roleLabel(u.role)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] font-bold text-zephyr-muted uppercase tracking-[0.5px] mb-[3px]">
                        Login Method
                      </div>
                      <div className="text-[13px]">
                        {derivedLoginMethodLabel(u.user_email || u.staff_user_email)}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={function () {
                        setViewing(null);
                        startEdit(u, "profiles");
                      }}
                      className="bg-zephyr-teal text-white border-0 rounded-[7px] py-[7px] px-4 text-[13px] cursor-pointer font-semibold"
                      style={{ fontFamily: "inherit" }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={function () {
                        sendPasswordReset(u.staff_user_email);
                      }}
                      className="bg-transparent border border-zephyr-border rounded-[7px] py-[7px] px-4 text-[13px] cursor-pointer text-zephyr-text"
                      style={{ fontFamily: "inherit" }}
                    >
                      📧 Send Password Reset
                    </button>
                    <button
                      onClick={function () {
                        resetUserMfa(u.id, u.staff_user_email);
                      }}
                      className="bg-transparent border border-zephyr-border rounded-[7px] py-[7px] px-4 text-[13px] cursor-pointer text-zephyr-text"
                      style={{ fontFamily: "inherit" }}
                    >
                      🔐 Reset MFA
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
      {filteredStaff.length === 0 && (
        <div className="p-10 text-center text-zephyr-muted text-[13px]">No staff found.</div>
      )}
      {editingProfile && (
        <StaffEditModal
          editing={editing}
          form={form}
          fv={fv}
          setF={setF}
          saving={saving}
          saveEdit={saveEdit}
          setEditing={setEditing}
          sendPasswordReset={sendPasswordReset}
          resetUserMfa={resetUserMfa}
          canManageDocs={canManageDocs}
        />
      )}
    </div>
  );
}
